import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { FAQ_ITEMS } from "@/lib/constants";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function About() {
  return (
    <div className="bg-neutral-light min-h-screen">
      {/* About Section */}
      <section id="about" className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center max-w-5xl mx-auto">
            <div>
              <h1 className="text-3xl font-bold mb-4">About NoBanks Nearby</h1>
              <p className="text-gray-600 mb-4">
                As a music producer known as "NoBanks Nearby," I've created a collection of 500+ original tracks designed specifically for creators like you.
              </p>
              <p className="text-gray-600 mb-4">
                My music is 100% original and copyright-clear, allowing you to create content without worrying about claims or strikes. I understand the struggles creators face finding quality music, which is why I built NoLicense Nearby.
              </p>
              <p className="text-gray-600 mb-6">
                Whether you're a social media influencer, filmmaker, or TV producer, I have tracks that will elevate your projects and help you tell your story.
              </p>
              <div className="flex space-x-4">
                <Link href="/catalog">
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                    Browse Catalog
                  </Button>
                </Link>
                <Link href="/license">
                  <Button variant="link" className="text-gray-700 hover:text-primary">
                    Contact Me
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&h=700&q=80" 
                alt="Music producer in studio" 
                className="rounded-lg shadow-lg w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg">
                <div className="flex items-center space-x-3">
                  <div className="text-primary">
                    <i className="fas fa-music text-2xl"></i>
                  </div>
                  <div>
                    <p className="font-bold">500+ Original Tracks</p>
                    <p className="text-sm text-gray-600">Ready for licensing</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 md:py-16 bg-neutral-light">
        <div className="container mx-auto px-4">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-bold mb-4">What Creators Are Saying</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Don't just take our word for it. Here's what creators who have licensed our music have to say.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <img
                  src="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=80&h=80&q=80"
                  alt="Sarah J."
                  className="w-12 h-12 rounded-full mr-4"
                />
                <div>
                  <h4 className="font-bold">Sarah J.</h4>
                  <p className="text-sm text-gray-500">Travel Vlogger</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">"The licensing process was so simple, and the music perfectly matched the vibe of my travel videos. My audience keeps asking about the amazing soundtrack!"</p>
              <div className="flex text-yellow-400">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <img
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=80&h=80&q=80"
                  alt="Mark T."
                  className="w-12 h-12 rounded-full mr-4"
                />
                <div>
                  <h4 className="font-bold">Mark T.</h4>
                  <p className="text-sm text-gray-500">Independent Filmmaker</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">"Finding quality music that won't break my indie film budget was a game-changer. The custom license was perfect for our festival submissions."</p>
              <div className="flex text-yellow-400">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star-half-alt"></i>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <img
                  src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=80&h=80&q=80"
                  alt="Jennifer K."
                  className="w-12 h-12 rounded-full mr-4"
                />
                <div>
                  <h4 className="font-bold">Jennifer K.</h4>
                  <p className="text-sm text-gray-500">TV Producer</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">"The AI-powered licensing system made it incredibly easy to find and license tracks for our series. The music quality is outstanding and very versatile."</p>
              <div className="flex text-yellow-400">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Use Cases Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-bold mb-4">Featured Projects</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">See how creators are using our music in their projects. From viral social media videos to TV productions.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-xl overflow-hidden shadow-md">
              <div className="relative h-56">
                <img
                  src="https://images.unsplash.com/photo-1557838923-2985c318be48?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=225&q=80"
                  alt="Social media influencer"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <p className="text-sm font-medium mb-1">Social Media</p>
                  <h3 className="text-lg font-bold">TravelWithJamie</h3>
                  <p className="text-sm opacity-80">Used "Horizon" for travel vlogs</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden shadow-md">
              <div className="relative h-56">
                <img
                  src="https://images.unsplash.com/photo-1492724441997-5dc865305da7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=225&q=80"
                  alt="TV production"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <p className="text-sm font-medium mb-1">TV Production</p>
                  <h3 className="text-lg font-bold">Cityscape Series</h3>
                  <p className="text-sm opacity-80">Used "Urban Flow" for opening credits</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden shadow-md">
              <div className="relative h-56">
                <img
                  src="https://images.unsplash.com/photo-1574717024453-e599429d6d33?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=225&q=80"
                  alt="Film production"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <p className="text-sm font-medium mb-1">Independent Film</p>
                  <h3 className="text-lg font-bold">The Last Journey</h3>
                  <p className="text-sm opacity-80">Used "Ethereal Morning" for key scene</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-neutral-light">
        <div className="container mx-auto px-4">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Got questions about licensing our music? Find answers to common questions below.</p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {FAQ_ITEMS.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-lg overflow-hidden"
                >
                  <AccordionTrigger className="px-6 py-4 text-left font-medium">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-gray-600">{item.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-10">
              <p className="text-gray-600 mb-4">Still have questions?</p>
              <Link href="/license">
                <Button variant="link" className="text-primary hover:text-primary-dark font-medium inline-flex items-center">
                  Contact Support
                  <i className="fas fa-arrow-right ml-2"></i>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-primary to-purple-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to License Music for Your Project?</h2>
          <p className="text-xl text-white opacity-90 mb-8 max-w-2xl mx-auto">Join hundreds of creators who have found the perfect soundtrack with NoLicense Nearby.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/catalog">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90">
                Browse Catalog
              </Button>
            </Link>
            <Link href="/license#top">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white/10"
                onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
              >
                Start Licensing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
